import capitalize from 'lodash/capitalize';
import React from 'react';
import { Box, Stack, Text } from '@atlaskit/primitives';
import ProgressBar from '@atlaskit/progress-bar';
import { token } from '@atlaskit/tokens';
import { getUserTier } from '@atlassian/cssxp-utils-entitlement';
import { validateFreeSubscriptions } from '@css-xp/entitlements-v2';
import { EntitlementDetails, BillingFrequency } from '@css-xp/graphtypes/src/generated-types-new';
import { getChargeElementV2, ChargeElementType } from '@css-xp/ppc-utils';
import { cardStyles, HeadingText, ProgressBarContainer } from './entitlement-cards.styles';

type UsageProps = {
  entitlement: EntitlementDetails;
};

export const Usage = ({ entitlement }: UsageProps) => {
  const { subscription, productId, edition, billingFrequency } = entitlement;

  const activeUsers = entitlement.activeUsers ?? 0;
  const chargeElement = entitlement.chargeElement ?? ChargeElementType.USER;
  const isFreePlan = validateFreeSubscriptions(entitlement);
  const isSandbox = entitlement.isSandbox;
  const isAnnualBilling = billingFrequency === BillingFrequency.ANNUAL;

  const usage = activeUsers / getUserTier(subscription) || 0;
  let progressTrackerBackground: string = token('color.border.information');
  if (usage > 0.85 && usage < 1) {
    progressTrackerBackground = token('color.border.warning');
  } else if (usage >= 1) {
    progressTrackerBackground = token('color.border.danger');
  }

  const chargeElementText = capitalize(
    getChargeElementV2({
      productId: productId as string,
      labelPlural: true,
      edition: edition as string,
      chargeElement,
    })
  );
  const shouldShowProgressTracker = !isFreePlan && !isSandbox && isAnnualBilling;

  return (
    <Box padding="space.200" xcss={cardStyles}>
      <Stack space="space.100" alignBlock="center">
        <Text size="small" color="color.text.subtlest">
          Quantity
        </Text>
        <>
          <HeadingText>
            {!shouldShowProgressTracker ? activeUsers : `${activeUsers} / ${getUserTier(subscription)}`}
          </HeadingText>
          {shouldShowProgressTracker && (
            <ProgressBarContainer backgroundColor={progressTrackerBackground}>
              <ProgressBar ariaLabel={`${usage * 100}% usage`} value={usage} />
            </ProgressBarContainer>
          )}
          {!shouldShowProgressTracker ? (
            <Text color="color.text.subtle">Total {chargeElementText.toLowerCase()}</Text>
          ) : (
            <Text color="color.text.subtle">{chargeElementText}</Text>
          )}
        </>
      </Stack>
    </Box>
  );
};
